import React, { useCallback, useState } from 'react';
import { ColumnLayout, Container, Header, ContentLayout, SpaceBetween, Textarea, Form, Input, Button, FormField } from '@cloudscape-design/components';
import { useNavigate } from 'react-router-dom';
import useChangeDetector from '../../../utils/useChangeDetector';
import { useApiWithFlash } from '../../../utils/hooks/useApiWithFlashInstance';
import { useAppSession } from '../../../utils/hooks/sessionContext';
import { TagIcon } from '../../utilities/tagIcon';
import { isValidTag } from '../../../utils/validations';
import { PartialClosureSummary } from '../../../types/closures';

export default function ClosuresCreate() {
  // state to control changes made
  const { item, setItem } = useChangeDetector<PartialClosureSummary>({ feature: '', description: '', tag: '' });

  // state
  const [hasResourceError, setHasResourceError] = useState<boolean>(true);
  const [resourceError, setResourceError] = useState<string>('');
  const [tagError, setTagError] = useState<boolean>(false);
  const [tagErrorMessage, setTagErrorMessage] = useState<string>();
  const [formSubmited, setFormSubmited] = useState<boolean>(false);

  // Hooks
  const navigate = useNavigate();
  const { handleApiWithFlash } = useApiWithFlash();
  const appSession = useAppSession();
  // Functions to handle form changes
  const handleDropdownChange = useCallback(
    (field: any, value: any) => {
      setItem((prev: any) => ({ ...prev, [field]: value }));
    },
    [setItem],
  );


  const validateTag = (value: string) => {
    // Define patterns
    const validTag = isValidTag(value);

    if (!validTag) {
      setTagError(true);
      setTagErrorMessage('Only lowercase alphanumeric, - _ and + supported');
    } else {
      setTagError(false);
      setTagErrorMessage('');
    }
  };

  const validateResource = (value: string) => {
    // Define patterns
    const generalPattern = /^[a-zA-Z0-9+_-]+$/;

    if (value.length >= 1) {
      if (!generalPattern.test(value)) {
        setHasResourceError(true);
        setResourceError('Only alphanumeric, - _ and + supported. Min 1 characters required');
      } else {
        setHasResourceError(false);
        setResourceError('');
      }
    }
  };

  const handleResourceChange = (value: string) => {
    validateResource(value);
    handleDropdownChange('feature', value);
  };

  const handleDescriptionChange = (value: string) => {
    handleDropdownChange('description', value);
  };

  const handleTagChange = (tag: string) => {
    handleDropdownChange('tag', tag );
    validateTag(tag);
  };


  // Handle form submission
  async function handleSubmitForm() {
    setFormSubmited(true);

    const payload = {
      feature: item.feature,
      description: item.description || '',
      data: {
        timezone: appSession?.instanceSelected?.instanceTimezone,
      },
      tag: item.tag
    };

    const response = await handleApiWithFlash(`/closures/${payload.feature}`, 'POST', {
      successMessage: `Successfully created closure group  ${payload.feature}`,
      errorMessage: `Error creating closure group ${payload.feature}`,
      data: payload,
    });

    setFormSubmited(false);

    if (response?.success) {
      navigate(`/closures/edit/${payload.feature}`);
    } else {
      navigate(`/closures`);
    }
  }

  const canSubmit = !hasResourceError && !tagError;

  return (
    <ContentLayout header={<Header variant='h1'></Header>}>
      <Container
        header={
          <Header
            variant='h2'
            description='Use this form to create a new Closure Group. You will add closures and attributes on the next screen'
          >
            Create a new Closure Group
          </Header>
        }
      >
        <form
          onSubmit={(e) => {
            console.log(e);
            e.preventDefault();
            handleSubmitForm();
          }}
        >
          <Form
            variant='embedded'
            actions={
              <SpaceBetween
                direction='horizontal'
                size='xs'
              >
                <Button
                  formAction='none'
                  variant='link'
                  onClick={() => navigate('/closures')}
                >
                  Cancel
                </Button>
                <Button
                  variant='primary'
                  loading={formSubmited}
                  disabled={!canSubmit}
                >
                  Submit
                </Button>
              </SpaceBetween>
            }
          >
            <SpaceBetween
              direction='vertical'
              size='xl'
            >
              <ColumnLayout
                columns={2}
                variant='text-grid'
              >
                <FormField
                  constraintText={'Only alphanumeric, - _ and + supported'}
                  label={'Provide a closure group name'}
                  errorText={resourceError}
                >
                  <Input
                    onChange={({ detail }) => handleResourceChange(detail.value)}
                    value={item.feature}
                  />
                </FormField>
                <FormField
                  constraintText={'Only alphanumeric, - _ and + supported'}
                  errorText={tagErrorMessage}
                  label={
                    <>
                      Tag your message group <TagIcon />
                    </>
                  }
                >
                  <Input
                    onChange={({ detail }) => handleTagChange(detail.value)}
                    value={item.tag ?? ''}
                  />
                </FormField>
              </ColumnLayout>
              <FormField label='Description' stretch>
                  <Textarea
                    onChange={({ detail }) => handleDescriptionChange(detail.value)}
                    value={item.description}
                  />
                </FormField>
            </SpaceBetween>
          </Form>
        </form>
      </Container>
    </ContentLayout>
  );
}
